import { Button, InfoBlock, ListBook } from '@arvoreeducacao/bonsai-ds'
import React, { useState } from 'react'
import { useTranslation } from 'i18n'
import * as S from './SearchResultsList.styles'
import { useQuery } from '@apollo/client'
import { searchBook } from 'api/queries'
import useDebounce from 'utils/useDebounce/useDebounce'
import SpinnerLoader from 'components/loader'
import { redirectToIntermediatePage } from 'utils/identify-modules/IdentifyModules'
import { useHistory } from 'react-router-dom'
import { OTTO_3D } from 'images/images3d'
import { saveTermOnHistory } from '../SearchHistory/localStorageHistory'
import { useFeatureTogglesStore } from 'stores/featureTogglesStore'
import SuggestBookModal from 'screens/site/search-results/components/SuggestBookModal'

export type SearchResultsListProps = {
  inputValue: string
  handleSubmit: (searchValue: string) => void
  onSelectBook?: () => void
  showSuggestBook?: boolean
}

const SearchResultsList: React.FC<SearchResultsListProps> = ({
  inputValue,
  handleSubmit,
  onSelectBook,
  showSuggestBook = false
}) => {
  const { t } = useTranslation()
  const debouncedSearch = useDebounce(inputValue, 500)
  const history = useHistory()
  const { featureToggles } = useFeatureTogglesStore()
  const canSuggestBook =
    showSuggestBook && Boolean(featureToggles?.bookRequests)
  const [isSuggestBookModalOpen, setIsSuggestBookModalOpen] = useState(false)

  const openBook = (slug: string) => {
    saveTermOnHistory(inputValue)
    redirectToIntermediatePage(history, 'book', slug)
    onSelectBook?.()
  }

  const suggestBookBlock = canSuggestBook && (
    <S.SuggestBookBlock>
      <S.SuggestBookText>
        {t(
          'Não encontrou o que procurava? Sugira este título para a equipe da biblioteca.'
        )}
      </S.SuggestBookText>
      <Button
        theme={{ type: 'secondary', size: 'large' }}
        text={t('Sugerir título')}
        onClick={() => setIsSuggestBookModalOpen(true)}
      />
    </S.SuggestBookBlock>
  )

  const suggestBookModal = canSuggestBook && (
    <SuggestBookModal
      isOpen={isSuggestBookModalOpen}
      onClose={() => setIsSuggestBookModalOpen(false)}
      initialBookName={inputValue || ''}
    />
  )

  const { loading, data, error } = useQuery(searchBook, {
    variables: {
      searchTerm: debouncedSearch,
      perPage: 3
    }
  })

  const books = data?.searchBookV3?.books

  return loading ? (
    <SpinnerLoader key="spinner-loader" data-testid="spinner-loader" />
  ) : error || books?.length === 0 ? (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        padding: '8px'
      }}
    >
      <InfoBlock
        image={OTTO_3D.SAD}
        title={t('Nenhum resultado encontrado')}
        subtitle={
          canSuggestBook
            ? t(
                'Não encontrou o que procurava? Sugira este título para a equipe da biblioteca.'
              )
            : t(
                'Tente novamente escrevendo o termo da busca de outra forma ou usando outra palavra-chave.'
              )
        }
      />
      {canSuggestBook && (
        <S.SuggestButtonWrapper>
          <Button
            theme={{ type: 'secondary', size: 'large' }}
            text={t('Sugerir título')}
            onClick={() => setIsSuggestBookModalOpen(true)}
          />
        </S.SuggestButtonWrapper>
      )}
      {suggestBookModal}
    </div>
  ) : (
    books?.length !== 0 && (
      <S.SearchResultsListWrapper>
        <S.ContentList>
          {data?.searchBookV3?.books?.map(book => {
            const { slug, author } = book

            return (
              <div
                data-testid="list-item-book"
                key={book.slug}
                role="button"
                tabIndex={0}
                onClick={() => openBook(slug)}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    openBook(slug)
                  }
                }}
              >
                <ListBook
                  title={book.name}
                  subtitle={author}
                  coverThumb={book.imageUrlThumbLow}
                  onClickButton={() => null}
                />
              </div>
            )
          })}
        </S.ContentList>
        <S.ButtomSearch
          text={t('Ver todos os resultados')}
          theme={{ type: 'ghost', size: 'xLarge' }}
          onClick={() => {
            saveTermOnHistory(inputValue)
            handleSubmit(inputValue)
          }}
        />
        {suggestBookBlock}
        {suggestBookModal}
      </S.SearchResultsListWrapper>
    )
  )
}

export default SearchResultsList
